/**
 * DIY-провайдер Keycloak (замена `@react-keycloak/web`).
 *
 * Делает один `keycloak.init(...)` при монтировании, прокидывает
 * `{ keycloak, initialized }` через React Context. Хук `useKeycloak()`
 * вытаскивает контекст.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import keycloak, { KEYCLOAK_INIT_OPTIONS } from './keycloak';
import { isKeycloakConfigured } from '../consts/env';
import type KeycloakJS from 'keycloak-js';

interface KeycloakContextValue {
  keycloak: KeycloakJS;
  initialized: boolean;
}

const KeycloakContext = createContext<KeycloakContextValue | null>(null);

interface KeycloakProviderProps {
  children: ReactNode;
  /** Перекрывается в тестах, чтобы не дёргать реальный Keycloak. */
  initFn?: (kc: KeycloakJS) => Promise<boolean>;
}

const defaultInit = (kc: KeycloakJS) => {
  // realm/clientId без дефолта (consts/env.ts): пусто = бандл собран без
  // env-файла стенда. keycloak-js упадёт и сам, но с невнятной ошибкой —
  // пишем причину до init, чтобы её было видно в консоли браузера.
  if (!isKeycloakConfigured()) {
    console.error(
      'Keycloak не сконфигурирован: REACT_APP_KEYCLOAK_REALM / ' +
        '_CLIENT_ID пусты. Образ собран без env-файла стенда ' +
        '(deploy/env/<region>[-prod].env).',
    );
  }
  return kc.init(KEYCLOAK_INIT_OPTIONS);
};

export const KeycloakProvider = ({
  children,
  initFn = defaultInit,
}: KeycloakProviderProps) => {
  const [initialized, setInitialized] = useState(false);
  // React 19 StrictMode дважды монтирует — guard от двойного init.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    initFn(keycloak)
      .then(() => setInitialized(true))
      .catch((err: unknown) => {
        console.error('Keycloak init failed', err);
      });
  }, [initFn]);

  return (
    <KeycloakContext.Provider value={{ keycloak, initialized }}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = (): KeycloakContextValue => {
  const ctx = useContext(KeycloakContext);
  if (!ctx) {
    throw new Error('useKeycloak must be used inside <KeycloakProvider>');
  }
  return ctx;
};
